import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query'
import {
  fetchApiWithToken,
  hydraNextPageParam,
  type WithHydraPagination
} from '~/api/utils'
import type { Invoice, InvoiceListFilters as InvoiceFilters } from '../types'
import { buildInvoiceListApiParams } from './invoiceListParams'

export type InvoicesLDResponse = WithHydraPagination<Invoice>

export const useInvoiceListWithFilters = ({
  enabled = true,
  itemsPerPage = 30,
  filters = {}
}: {
  enabled?: boolean
  itemsPerPage?: number
  filters?: InvoiceFilters
}): {
  invoicelist: Invoice[] | undefined
  invoicelistTotal: number | undefined
  isInvoiceListLoading: boolean
  isInvoiceListError: boolean
  fetchNextInvoiceListPage: () => void
  hasNextInvoiceListPage: boolean
  isFetchingNextInvoiceListPage: boolean
} => {
  const params = buildInvoiceListApiParams(filters)

  const {
    data,
    isLoading: isInvoiceListLoading,
    fetchNextPage: fetchNextInvoiceListPage,
    hasNextPage: hasNextInvoiceListPage,
    isError: isInvoiceListError,
    isFetchingNextPage: isFetchingNextInvoiceListPage
  } = useInfiniteQuery<
    InvoicesLDResponse,
    Error,
    InfiniteData<InvoicesLDResponse>
  >({
    queryKey: ['invoices', 'search', params.toString()],
    initialPageParam: 1,
    getNextPageParam: hydraNextPageParam(itemsPerPage),
    queryFn: ({ pageParam, signal }) => {
      return fetchApiWithToken<InvoicesLDResponse>({
        method: 'get',
        url: `/v1/invoices?page=${pageParam}&itemsPerPage=${itemsPerPage}&${params.toString()}`,
        useLDJson: true,
        signal
      })
    },
    refetchOnWindowFocus: false,
    staleTime: 1800_000,
    enabled
  })

  return {
    invoicelist: data?.pages.flatMap((page) => page['hydra:member']),
    invoicelistTotal: data?.pages[0]?.['hydra:totalItems'],
    isInvoiceListLoading,
    isInvoiceListError,
    fetchNextInvoiceListPage,
    hasNextInvoiceListPage,
    isFetchingNextInvoiceListPage
  }
}
